import React from 'react';
import Input from '../../../components/ui/Input';

import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const ContactPreferencesStep = ({ formData, updateFormData, errors }) => {
  const contactMethods = [
    { value: 'email', label: 'E-mail', icon: 'Mail' },
    { value: 'phone', label: 'Telefoon', icon: 'Phone' },
    { value: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle' },
    { value: 'any', label: 'Maakt niet uit', icon: 'Users' }
  ];

  const availabilityOptions = [
    { value: 'weekday-morning', label: 'Doordeweeks ochtend (9:00-12:00)' },
    { value: 'weekday-afternoon', label: 'Doordeweeks middag (12:00-17:00)' },
    { value: 'weekday-evening', label: 'Doordeweeks avond (17:00-20:00)' },
    { value: 'weekend', label: 'Weekend' },
    { value: 'flexible', label: 'Flexibel' }
  ];

  const handleAvailabilityToggle = (window) => {
    const current = formData?.availabilityWindows || [];
    const updated = current?.includes(window)
      ? current?.filter(w => w !== window)
      : [...current, window];
    updateFormData({ availabilityWindows: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Contactgegevens</h2>
        <p className="text-muted-foreground">Hoe kunnen providers u bereiken?</p>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <Input
          label="Volledige naam"
          required
          placeholder="Jan de Vries"
          value={formData?.customerName}
          onChange={(e) => updateFormData({ customerName: e?.target?.value })}
          error={errors?.customerName}
        />

        <Input
          label="E-mailadres"
          required
          type="email"
          placeholder="jan@voorbeeld.nl"
          value={formData?.customerEmail}
          onChange={(e) => updateFormData({ customerEmail: e?.target?.value })}
          error={errors?.customerEmail}
        />

        <Input
          label="Telefoonnummer"
          required
          type="tel"
          placeholder="06 12345678"
          value={formData?.phone}
          onChange={(e) => updateFormData({ phone: e?.target?.value })}
          error={errors?.phone}
          description="Voor snelle communicatie en afspraken"
        />
      </div>

      {/* Preferred Contact Method */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Voorkeur contactmethode
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {contactMethods?.map(method => (
            <button
              key={method?.value}
              type="button"
              onClick={() => updateFormData({ preferredContactMethod: method?.value })}
              className={cn(
                "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all hover:shadow-md",
                formData?.preferredContactMethod === method?.value
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              <Icon 
                name={method?.icon} 
                size={24} 
                className={formData?.preferredContactMethod === method?.value ? "text-primary" : "text-muted-foreground"} 
              />
              <span className={cn(
                "text-sm font-medium mt-2 text-center",
                formData?.preferredContactMethod === method?.value ? "text-primary" : "text-foreground"
              )}>
                {method?.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Availability Windows */}
      <div>
        <label className="text-sm font-medium text-foreground mb-3 block">
          Wanneer bent u het beste bereikbaar?
        </label>
        <div className="space-y-2">
          {availabilityOptions?.map(option => (
            <div
              key={option?.value}
              onClick={() => handleAvailabilityToggle(option?.value)}
              className="flex items-center p-3 rounded-lg border border-border bg-card hover:bg-accent cursor-pointer transition-colors"
            >
              <Checkbox
                checked={formData?.availabilityWindows?.includes(option?.value)}
                onCheckedChange={() => handleAvailabilityToggle(option?.value)}
              />
              <span className="ml-3 text-sm text-foreground">{option?.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <label htmlFor="additionalNotes" className="text-sm font-medium text-foreground mb-2 block">
          Extra opmerkingen (optioneel)
        </label>
        <textarea
          id="additionalNotes"
          rows={4}
          value={formData?.additionalNotes}
          onChange={(e) => updateFormData({ additionalNotes: e?.target?.value })}
          placeholder="Heeft u nog specifieke vragen of opmerkingen voor de providers?"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {/* Privacy & Terms */}
      <div className="border-t border-border pt-6 space-y-4">
        <div className="bg-info/10 border border-info/20 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-info flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-foreground font-medium mb-1">Uw gegevens zijn veilig</p>
            <p className="text-xs text-muted-foreground">
              Uw contactgegevens worden alleen gedeeld met gekwalificeerde providers die aan uw aanvraag kunnen voldoen. Wij verkopen uw gegevens nooit aan derden.
            </p>
          </div>
        </div>

        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-start space-x-3 mb-3">
            <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-foreground font-medium">Wat gebeurt er na verzending?</p>
            </div>
          </div>
          <ol className="text-xs text-muted-foreground space-y-2 ml-7">
            <li>1. Uw aanvraag wordt direct naar geschikte providers gestuurd</li>
            <li>2. Providers nemen binnen 1-3 werkdagen contact met u op</li>
            <li>3. U ontvangt vrijblijvende offertes en advies</li>
            <li>4. U kiest zelf met welke provider u verder gaat</li>
          </ol>
        </div>
      </div>

      {/* Final Summary */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
          <Icon name="FileText" size={18} className="mr-2 text-primary" />
          Samenvatting van uw aanvraag
        </h3>
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Project:</span>
            <span className="text-foreground font-medium">{formData?.serviceType || 'Niet geselecteerd'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Locatie:</span>
            <span className="text-foreground font-medium">{formData?.postalCode || 'Niet ingevuld'} {formData?.city || ''}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Budget:</span>
            <span className="text-foreground font-medium">{formData?.budgetRange || 'Niet geselecteerd'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Urgentie:</span>
            <span className="text-foreground font-medium capitalize">{formData?.urgency || 'medium'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPreferencesStep;